import type { KeyboardEvent } from "react";
import { edgeEnabled, pageDown, pageUp, stepEnabled } from "./optionNavigation";
import type { SelectOption } from "./selectOption";
import type { SelectList } from "./useSelectList";
import { isTypeaheadKey, useTypeahead } from "./useTypeahead";

/** Keys that open the closed list on the selected option. */
const OPENING_KEYS = ["ArrowDown", "ArrowUp", "Enter", " "];

interface SelectKeyboardInput {
  list: SelectList;
  options: SelectOption[];
  selectedIndex: number;
  searchable: boolean;
  disabled?: boolean;
  commit: (option: SelectOption | undefined) => void;
}

interface SelectKeyboard {
  onTriggerKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onSearchKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Keyboard per the WAI-ARIA select-only combobox. Closed trigger: ↑ ↓ Enter Space open, Home End
 * open on an end, a letter opens on the matching option (or starts the search). Open list: ↑ ↓
 * PageUp PageDown move, Enter selects, Alt+↑ selects, Tab closes; the trigger also takes Home End,
 * Space and typeahead, which the search box keeps for editing its text.
 */
export function useSelectKeyboard({ list, options, selectedIndex, searchable, disabled, commit }: SelectKeyboardInput): SelectKeyboard {
  const typeahead = useTypeahead();
  const { visible, active, setActive } = list;

  /** Keys the open list answers to from both the trigger and the search box; false if not one of them. */
  function handleListKey(e: KeyboardEvent<HTMLElement>): boolean {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!e.altKey) setActive((a) => (a < 0 ? edgeEnabled(visible, "first") : stepEnabled(visible, a, 1)));
        return true;
      case "ArrowUp":
        e.preventDefault();
        if (e.altKey) commit(visible[active]);
        else setActive((a) => (a < 0 ? edgeEnabled(visible, "last") : stepEnabled(visible, a, -1)));
        return true;
      case "PageDown":
        e.preventDefault();
        setActive((a) => pageDown(visible, a));
        return true;
      case "PageUp":
        e.preventDefault();
        setActive(pageUp);
        return true;
      case "Enter":
        e.preventDefault();
        commit(visible[active]);
        return true;
      case "Tab":
        list.dismiss();
        return true;
      default:
        return false;
    }
  }

  function handleOpenTriggerKey(e: KeyboardEvent<HTMLButtonElement>): void {
    if (handleListKey(e)) return;
    if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      setActive(edgeEnabled(visible, e.key === "Home" ? "first" : "last"));
    } else if (e.key === " ") {
      e.preventDefault();
      commit(visible[active]);
    } else if (isTypeaheadKey(e)) {
      const match = typeahead(e.key, visible, active);
      if (match >= 0) setActive(match);
    }
  }

  function handleClosedTriggerKey(e: KeyboardEvent<HTMLButtonElement>): void {
    if (OPENING_KEYS.includes(e.key)) {
      e.preventDefault();
      list.openList();
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      list.openList(edgeEnabled(options, e.key === "Home" ? "first" : "last"));
    } else if (isTypeaheadKey(e) && searchable) {
      e.preventDefault();
      list.openWithQuery(e.key);
    } else if (isTypeaheadKey(e)) {
      const match = typeahead(e.key, options, selectedIndex);
      if (match >= 0) list.openList(match);
    }
  }

  return {
    onTriggerKeyDown: (e) => {
      if (disabled) return;
      if (list.open) handleOpenTriggerKey(e);
      else handleClosedTriggerKey(e);
    },
    onSearchKeyDown: (e) => {
      handleListKey(e);
    },
  };
}
